import React from 'react';
import {
  Activity, Eye, Settings, Bell, Clock, Shield,
  TrendingUp, Zap, Database, BarChart3,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const AssetMonitoringPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Asset Monitoring',
      title: (
        <>
          Comprehensive asset monitoring <em>for renewable energy</em>
        </>
      ),
      lede: 'Monitor the health and performance of your renewable energy assets in real-time with our advanced monitoring platform. Gain complete visibility into your operations and optimize asset performance.',
      image: '/assets/img/general/solar-energy-7882650_1280.jpg',
      imageAlt: 'Solar energy installation under monitoring',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '24/7', label: 'Real-time monitoring' },
        { value: '99.9%', label: 'System uptime' },
        { value: '40%', label: 'Downtime reduction' },
        { value: '<1s', label: 'Data refresh rate' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A comprehensive monitoring platform',
      lede: 'Our asset monitoring platform provides all the tools you need to track, analyze, and optimize your renewable energy assets.',
      body: [
        'Every asset in your portfolio reports into a single operational view — generation, availability, equipment health and performance ratio, side by side and continuously refreshed.',
      ],
      checklist: [
        'Real-time asset performance monitoring and tracking',
        'Automated anomaly detection and alerting system',
        'Comprehensive health assessment for all equipment',
        'Historical data analysis and trend identification',
        'Integration with SCADA and existing monitoring systems',
        'Customizable dashboards and reporting tools',
        'Mobile access for on-the-go monitoring',
        'Multi-site portfolio management capabilities',
      ],
      image: '/assets/img/general/photovoltaic-system-2742302_1280.jpg',
      imageAlt: 'Photovoltaic system in the field',
      imageCaption: 'Real-time visibility into all your renewable energy assets from a single platform',
    }}
    capabilities={{
      eyebrow: 'Key features',
      title: 'Advanced monitoring features',
      lede: 'Comprehensive monitoring capabilities designed for renewable energy assets.',
      items: [
        {
          icon: Activity,
          title: 'Real-time monitoring',
          description: 'Continuous 24/7 monitoring of all renewable energy assets with instant data visualization and updates.',
        },
        {
          icon: Eye,
          title: 'Asset health tracking',
          description: 'Comprehensive health assessment of individual assets with predictive maintenance alerts.',
        },
        {
          icon: Settings,
          title: 'Performance metrics',
          description: 'Track key performance indicators including efficiency, availability, and production metrics.',
        },
        {
          icon: Bell,
          title: 'Smart alerts',
          description: 'Intelligent alert system that notifies you of anomalies and potential issues in real-time.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Monitoring metrics',
      title: 'Track critical metrics',
      lede: 'Monitor all essential metrics for optimal asset performance.',
      items: [
        {
          icon: Zap,
          title: 'Power generation',
          description: 'Track real-time and historical power output across all assets.',
        },
        {
          icon: Clock,
          title: 'Asset availability',
          description: 'Monitor uptime and availability metrics for each asset.',
        },
        {
          icon: Activity,
          title: 'Equipment health',
          description: 'Continuous health monitoring with predictive maintenance insights.',
        },
        {
          icon: BarChart3,
          title: 'Performance ratio',
          description: 'Analyze performance ratios and identify optimization opportunities.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Business benefits',
      title: 'Measurable business impact',
      lede: 'Drive operational excellence with comprehensive asset monitoring.',
      items: [
        {
          icon: Zap,
          title: 'Maximize uptime',
          description: 'Reduce downtime by up to 40% with proactive monitoring and early issue detection.',
        },
        {
          icon: TrendingUp,
          title: 'Optimize performance',
          description: 'Increase asset efficiency by 15-25% through continuous performance optimization.',
        },
        {
          icon: Shield,
          title: 'Risk mitigation',
          description: 'Identify and address potential risks before they impact your operations.',
        },
        {
          icon: Database,
          title: 'Data-driven insights',
          description: 'Leverage comprehensive data analytics for informed decision-making.',
        },
      ],
    }}
    cta={{
      title: 'Ready to monitor your assets?',
      body: 'Start optimizing your renewable energy operations with comprehensive asset monitoring.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default AssetMonitoringPage;
